import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../core/store";
import { TVesselInfo } from "./mapTypes";

const selectVesselsLastSignalSlice = (state: RootState) => state.vesselsLastSignalSlice;
const selectVesselOngoingVoyageSignalsSlice = (state: RootState) => state.vesselOngoingVoyageSignalsSlice;
const selectVesselDailyPositionsSlice = (state: RootState) => state.vesselDailyPositionsSlice;
const selectVesselLastVoyagesSlice = (state: RootState) => state.vesselLastVoyagesSlice;

export const selectVesselsLastSignalData = createSelector(
    [selectVesselsLastSignalSlice],
    (vesselsLastSignalSlice) => vesselsLastSignalSlice.vesselsLastSignalData
);

export const selectVesselsLastSignalLoading = createSelector(
    [selectVesselsLastSignalSlice],
    (vesselsLastSignalSlice) => vesselsLastSignalSlice.vesselsLastSignalLoading
);

export const selectVesselLastVoyagesData = createSelector(
    [selectVesselLastVoyagesSlice],
    (vesselLastVoyagesSlice) => vesselLastVoyagesSlice.vesselLastVoyagesData
);

export const selectVesselLastVoyagesLoading = createSelector(
    [selectVesselLastVoyagesSlice],
    (vesselLastVoyagesSlice) => vesselLastVoyagesSlice.vesselLastVoyagesLoading
);

export const selectVesselLastSignalInfoByImoNumber = createSelector(
    selectVesselsLastSignalSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (vesselsLastSignalSlice, imoNumber) => {
        if (!vesselsLastSignalSlice.vesselsLastSignalData
            || !vesselsLastSignalSlice.vesselsLastSignalData.features
            || vesselsLastSignalSlice.vesselsLastSignalData.features.length === 0)
            return null;
        const filtered = vesselsLastSignalSlice.vesselsLastSignalData?.features.filter((f) => f.properties?.imoNumber === imoNumber);
        return filtered && filtered.length > 0 ? filtered[0] : null;
    }
);

export const selectVesselOngoingVoyageSignalsByImoNumber = createSelector(
    selectVesselOngoingVoyageSignalsSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (vesselOngoingVoyageSignalsSlice, imoNumber) => {
        if (!vesselOngoingVoyageSignalsSlice.vesselOngoingVoyageSignalsData
            || !vesselOngoingVoyageSignalsSlice.vesselOngoingVoyageSignalsData.features
            || vesselOngoingVoyageSignalsSlice.vesselOngoingVoyageSignalsData.features.length === 0)
            return null;

        const filtered = vesselOngoingVoyageSignalsSlice.vesselOngoingVoyageSignalsData?.features.filter((f) => f.properties?.imoNumber === imoNumber);
        return filtered && filtered.length > 0 ? filtered[0] : null;
    }
);

export const selectVesselDailyPositionsWithoutLastSignalByImoNumber = createSelector(
    selectVesselsLastSignalSlice, selectVesselDailyPositionsSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (vesselsLastSignalSlice, vesselDailyPositionsSlice, imoNumber) => {

        if (!vesselsLastSignalSlice || !vesselsLastSignalSlice.vesselsLastSignalData || !vesselsLastSignalSlice.vesselsLastSignalData.features
            || vesselsLastSignalSlice.vesselsLastSignalData.features.length === 0) return;

        const lastSignal = vesselsLastSignalSlice.vesselsLastSignalData?.features.find((f) => f.properties?.imoNumber === imoNumber);

        if (!vesselDailyPositionsSlice.vesselDailyPositionsData || !vesselDailyPositionsSlice.vesselDailyPositionsData.features || vesselDailyPositionsSlice.vesselDailyPositionsData.features.length === 0) return;

        // filter dailypositiondata that not contains last signal by timestamp
        const filtered = vesselDailyPositionsSlice.vesselDailyPositionsData?.features.filter((f) =>
            f.properties?.imoNumber === imoNumber && f.properties?.timestamp !== lastSignal?.properties?.timestamp);

        // create TVeeselInfo object
        const vesselInfo = {
            type: "FeatureCollection",
            features: filtered
        } as TVesselInfo;

        return vesselInfo;
    }
);

export const selectVesselDailyPositionsAsLineString = createSelector(
    selectVesselDailyPositionsSlice,
    (vesselDailyPositionsSlice) => {

        if (!vesselDailyPositionsSlice.vesselDailyPositionsData
            || !vesselDailyPositionsSlice.vesselDailyPositionsData.features
            || vesselDailyPositionsSlice.vesselDailyPositionsData.features.length === 0) return null;

        // Collect geometries of daily positions in to one array, filtering out features without geometry
        const lineStringCoordinates = vesselDailyPositionsSlice.vesselDailyPositionsData?.features
            .filter((f) => f.geometry?.coordinates)
            .map((f) => f.geometry!.coordinates) as number[][];

        // create lineString object as TVesselInfo
        const vesselInfo = {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    properties: {
                        imoNumber: vesselDailyPositionsSlice.vesselDailyPositionsData?.features[0].properties?.imoNumber,
                        reportedEta: vesselDailyPositionsSlice.vesselDailyPositionsData?.features[0].properties?.reportedEta,
                        reportedDestination: vesselDailyPositionsSlice.vesselDailyPositionsData?.features[0].properties?.reportedDestination
                    },
                    geometry: {
                        type: "LineString",
                        coordinates: lineStringCoordinates
                    }
                }
            ]
        } as TVesselInfo;

        return vesselInfo;
    }
);